import { Alert, Button, Form, InputNumber, Radio, Select, Space, Switch, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { registrationTicketTypes } from '../utils/ticketTypeVisibility';

const { Text } = Typography;

/**
 * A `sponsors` config szekció szerkesztője (#157).
 *
 * A #148–#151 és a #156 minden viselkedése ezen a négy kapcsolón áll, de
 * felület eddig nem tartozott hozzájuk: a szervezőnek — és a tesztelőnek —
 * kézzel kimásolt JWT-vel kellett `PATCH /events/:id/config`-ot curl-öznie.
 * Két kapcsoló levelet küld vagy pénzt kér, tehát pont ezeknél a legrosszabb
 * a validálatlan, kézzel írt JSON.
 */

/** A backend `MAX_REMINDER_DAYS`-e (`sponsor-reminder-schedule.ts`). */
const MAX_REMINDER_DAYS = 5;
/** És ugyanonnan a nap-korlátok. A cron a listát amúgy is megszűri; ez azért
 *  van itt, hogy a szervező a MENTÉS előtt lássa, mit nem fogadunk el. */
const MIN_REMINDER_DAY = 1;
const MAX_REMINDER_DAY = 60;

export interface SponsorsSectionConfig {
  cardStyle: 'full' | 'compact';
  selfService: { enabled: boolean };
  paidExtraTickets: { enabled: boolean; ticketTypeId: string | null };
  reminderDaysBefore: number[];
}

interface TicketTypeOption {
  id: string;
  name: string;
  price: number;
  isPublic?: boolean;
}

interface EventSponsorSettingsProps {
  eventId: string;
  /** A `GET /events/:id/config` `sponsors` szekciója, default-okkal feloldva. */
  value: SponsorsSectionConfig;
  currency: string;
  saving: boolean;
  onSave: (section: SponsorsSectionConfig) => void;
}

/**
 * Amit a bekapcsolás előtt meg tudunk mondani.
 *
 * A kapcsolattartó e-mailek MEGLÉTÉT szándékosan nem soroljuk fel: sem a
 * publikus szponzor-lista, sem az összesítő nem adja ki a `contactEmail`-t
 * (#112 óta a szűkített vetület a szabály), tehát egy „hány szponzornak van
 * címe" szám csak találgatás lenne. A szervező a Szponzorok oldalon látja.
 */
interface SponsorReadiness {
  levels: number;
  sponsors: number;
}

/**
 * Az új emlékeztető-sor alapértéke: a szokásos 7, vagy — ha az már szerepel —
 * az első szabad nap.
 *
 * Egy fix `7` mellett a második kattintás azonnal ütköző sort adna, amit a
 * lista-validátor rögtön kifogásol. A szervező hibaüzenetet kapna azért, mert
 * megnyomott egy gombot.
 */
function nextReminderDay(existing: unknown): number {
  const used = new Set(Array.isArray(existing) ? existing : []);
  for (const day of [7, 2, 14, 30, 1]) {
    if (!used.has(day)) return day;
  }
  for (let day = MIN_REMINDER_DAY; day <= MAX_REMINDER_DAY; day++) {
    if (!used.has(day)) return day;
  }
  return MIN_REMINDER_DAY;
}

export function EventSponsorSettings({
  eventId,
  value,
  currency,
  saving,
  onSave,
}: EventSponsorSettingsProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm<SponsorsSectionConfig>();
  const [ticketTypes, setTicketTypes] = useState<TicketTypeOption[]>([]);
  // Külön a listától: üres lista + „betöltve" azt jelenti, hogy az eseménynek
  // TÉNYLEG nincs jegytípusa; üres lista + „nem töltődött be" azt, hogy nem
  // tudjuk. A kettőre más figyelmeztetés jár, és a különbséget a `[]` önmagában
  // nem hordozza.
  const [ticketTypesLoaded, setTicketTypesLoaded] = useState(false);
  const [readiness, setReadiness] = useState<SponsorReadiness | null>(null);

  // A kapcsolók egymást vezérlik (a fizetős jegytípus csak bekapcsolva
  // kötelező), ezért a saját állapotukat a formból olvassuk, nem a propból.
  const paidEnabled = Form.useWatch(['paidExtraTickets', 'enabled'], form) ?? false;
  const selfServiceEnabled = Form.useWatch(['selfService', 'enabled'], form) ?? false;

  useEffect(() => {
    form.setFieldsValue(value);
  }, [form, value]);

  useEffect(() => {
    let cancelled = false;

    // KÉT külön kérés-csoport, szándékosan nem egy `Promise.all` egy
    // `catch`-csel. A jegytípusok a fizetendő összeg forrását adják, a másik
    // kettő csak egy figyelmeztetéshez kell. Közös hibaágon egy elszálló
    // `sponsor-levels` hívás üresen hagyná a jegytípus-választót — a szervező
    // üres legördülőt látna, miközben a már beállított `ticketTypeId` átmegy a
    // `required` szabályon, tehát az űrlap menthetőnek látszana anélkül, hogy
    // az értéket meg lehetne változtatni.
    const loadTicketTypes = async () => {
      try {
        const types = await api.get(`/tickets/types/event/${eventId}`);
        if (cancelled) return;
        // A hidden configured type then shows the missing-type warning.
        setTicketTypes(registrationTicketTypes<TicketTypeOption>(types.data ?? []));
        setTicketTypesLoaded(true);
      } catch {
        if (!cancelled) setTicketTypesLoaded(false);
      }
    };

    const loadReadiness = async () => {
      try {
        const [levels, sponsors] = await Promise.all([
          api.get(`/events/${eventId}/sponsor-levels`),
          api.get('/sponsors', { params: { eventId } }),
        ]);
        if (cancelled) return;
        setReadiness({
          levels: (levels.data ?? []).length,
          sponsors: (sponsors.data ?? []).length,
        });
      } catch {
        // A figyelmeztetés kényelmi: ha nem jön meg, a fül működjön nélküle.
        if (!cancelled) setReadiness(null);
      }
    };

    loadTicketTypes();
    loadReadiness();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleFinish = (values: SponsorsSectionConfig) => {
    onSave({
      cardStyle: values.cardStyle,
      selfService: { enabled: !!values.selfService?.enabled },
      paidExtraTickets: {
        enabled: !!values.paidExtraTickets?.enabled,
        // Kikapcsolva a jegytípus null: így nem marad a configban egy
        // jegytípus-hivatkozás, ami egy későbbi bekapcsolásnál némán
        // érvénybe lépne.
        ticketTypeId: values.paidExtraTickets?.enabled
          ? (values.paidExtraTickets?.ticketTypeId ?? null)
          : null,
      },
      reminderDaysBefore: (values.reminderDaysBefore ?? []).filter(
        (d): d is number => typeof d === 'number' && Number.isFinite(d)
      ),
    });
  };

  const notReady =
    selfServiceEnabled &&
    readiness !== null &&
    (readiness.levels === 0 || readiness.sponsors === 0);

  // A beállított jegytípus időközben törölhető a Jegytípusok képernyőn. A
  // `required` szabály ilyenkor is átmegy (van érték), a választó viszont a
  // nyers azonosítót írja ki címke helyett, tehát a fül rendben lévőnek
  // mutatná a fizetős beállítást. A hibát a SZPONZOR találná meg: a beküldés
  // `resolveTicketTypes`-nál 400-zal elszáll, az ár pedig `null`.
  const configuredTicketTypeId = value.paidExtraTickets?.ticketTypeId ?? null;
  const ticketTypeMissing =
    paidEnabled &&
    ticketTypesLoaded &&
    configuredTicketTypeId !== null &&
    !ticketTypes.some((tt) => tt.id === configuredTicketTypeId);

  return (
    <Form form={form} layout="vertical" initialValues={value} onFinish={handleFinish}>
      <Form.Item label={t('eventSettings.sponsorCardStyleLabel')} name="cardStyle">
        <Radio.Group
          options={[
            { label: t('eventSettings.sponsorCardStyleFull'), value: 'full' },
            { label: t('eventSettings.sponsorCardStyleCompact'), value: 'compact' },
          ]}
          optionType="button"
        />
      </Form.Item>
      <Text type="secondary">{t('eventSettings.sponsorCardStyleHint')}</Text>

      <Form.Item
        style={{ marginTop: 24 }}
        label={t('eventSettings.sponsorSelfServiceLabel')}
        name={['selfService', 'enabled']}
        valuePropName="checked"
        extra={t('eventSettings.sponsorSelfServiceExtra')}
      >
        <Switch />
      </Form.Item>

      {notReady && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message={t('eventSettings.sponsorNotReadyTitle')}
          description={
            readiness?.levels === 0
              ? t('eventSettings.sponsorNotReadyLevels')
              : t('eventSettings.sponsorNotReadySponsors')
          }
        />
      )}

      <Form.Item
        label={t('eventSettings.sponsorPaidExtraLabel')}
        name={['paidExtraTickets', 'enabled']}
        valuePropName="checked"
        extra={t('eventSettings.sponsorPaidExtraExtra')}
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label={t('eventSettings.sponsorPaidTicketTypeLabel')}
        name={['paidExtraTickets', 'ticketTypeId']}
        // A jegytípus a FIZETENDŐ ÖSSZEG egyetlen forrása. Bekapcsolva,
        // kiválasztott jegytípus nélkül a szponzor keret feletti helye
        // fizetési felszólítás nélkül maradna, tehát a mentést itt állítjuk
        // meg — nem a szerveren, ahol ma semmi nem köti.
        rules={[
          {
            required: paidEnabled,
            message: t('eventSettings.sponsorPaidTicketTypeRequired'),
          },
        ]}
        extra={t('eventSettings.sponsorPaidTicketTypeExtra')}
      >
        <Select
          allowClear
          disabled={!paidEnabled}
          style={{ maxWidth: 480 }}
          placeholder={t('eventSettings.sponsorPaidTicketTypePlaceholder')}
          options={ticketTypes.map((tt) => ({
            value: tt.id,
            label: `${tt.name} — ${tt.price} ${currency}`,
          }))}
        />
      </Form.Item>

      {ticketTypeMissing && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message={t('eventSettings.sponsorTicketTypeMissingTitle')}
          description={t('eventSettings.sponsorTicketTypeMissingBody')}
        />
      )}

      <Form.Item
        label={t('eventSettings.sponsorRemindersLabel')}
        extra={t('eventSettings.sponsorRemindersExtra', {
          min: MIN_REMINDER_DAY,
          max: MAX_REMINDER_DAY,
          count: MAX_REMINDER_DAYS,
        })}
      >
        <Form.List
          name="reminderDaysBefore"
          // A backend `normalizeReminderDays`-e kiszűri az ismétlődést, tehát
          // `[7, 7]` egyetlen emlékeztetőt jelent. A szervező viszont két sort
          // látna, és azt hinné, kettő megy ki — a listát ezért itt kötjük
          // ahhoz, amit a cron ténylegesen tenni fog.
          rules={[
            {
              validator: async (_rule, days: (number | undefined)[] = []) => {
                const given = days.filter((d): d is number => typeof d === 'number');
                if (new Set(given).size !== given.length) {
                  throw new Error(t('eventSettings.sponsorRemindersDuplicate'));
                }
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map((field) => (
                <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                  <Form.Item
                    name={field.name}
                    rules={[
                      {
                        required: true,
                        type: 'integer',
                        min: MIN_REMINDER_DAY,
                        max: MAX_REMINDER_DAY,
                        message: t('eventSettings.sponsorRemindersInvalid', {
                          min: MIN_REMINDER_DAY,
                          max: MAX_REMINDER_DAY,
                        }),
                      },
                    ]}
                  >
                    <InputNumber
                      min={MIN_REMINDER_DAY}
                      max={MAX_REMINDER_DAY}
                      addonAfter={t('eventSettings.sponsorRemindersUnit')}
                    />
                  </Form.Item>
                  <Button danger onClick={() => remove(field.name)}>
                    {t('eventSettings.delete')}
                  </Button>
                </Space>
              ))}
              <Button
                type="dashed"
                disabled={fields.length >= MAX_REMINDER_DAYS}
                onClick={() => add(nextReminderDay(form.getFieldValue('reminderDaysBefore')))}
              >
                {t('eventSettings.sponsorRemindersAdd')}
              </Button>
              <Form.ErrorList errors={errors} />
            </>
          )}
        </Form.List>
      </Form.Item>

      <Button type="primary" htmlType="submit" loading={saving}>
        {t('eventSettings.saveSponsors')}
      </Button>
    </Form>
  );
}

export default EventSponsorSettings;
